// src/theme.js — TRM Medical · Verde Petrolio palette
// Primary: #0D5C54 → #00897B · Acqua: #00C4A7
import { createTheme } from '@mui/material/styles';

export const createAppTheme = (prefersDark) => createTheme({
  palette: {
    mode: prefersDark ? 'dark' : 'light',
    primary: {
      main:         '#00897B',
      dark:         '#0D5C54',
      light:        '#00C4A7',
      contrastText: '#ffffff',
    },
    secondary: {
      main:         '#7C3AED',
      light:        '#A855F7',
      contrastText: '#ffffff',
    },
    background: {
      default: prefersDark ? '#0B2D36' : '#FFFFFF',
      paper:   prefersDark ? '#104448' : '#FFFFFF',
    },
    text: {
      primary:   prefersDark ? '#00C4A7' : '#0D2B27',
      secondary: prefersDark ? 'rgba(0,196,167,0.70)' : '#3D6B64',
    },
    error:   { main: '#EF4444' },
    warning: { main: '#F59E0B' },
    success: { main: '#059669', light: '#10B981', contrastText: '#ffffff' },
    info:    { main: '#00897B' },
    divider: prefersDark ? '#1A5A68' : '#B2DDD8',
  },
  typography: {
    fontFamily: "'Poppins', 'Arial', sans-serif",
    h4: {
      fontFamily:    "'Syne', 'Poppins', sans-serif",
      fontWeight:    800,
      fontSize:      '2rem',
      letterSpacing: '-0.02em',
    },
    h5: {
      fontFamily:    "'Syne', 'Poppins', sans-serif",
      fontWeight:    700,
      letterSpacing: '-0.015em',
    },
    h6: {
      fontFamily:    "'Syne', 'Poppins', sans-serif",
      fontWeight:    700,
      fontSize:      '1.4rem',
      letterSpacing: '-0.01em',
    },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    overline:  { letterSpacing: '0.14em', fontWeight: 600 },
    body1:     { fontSize: '0.95rem' },
    body2:     { fontSize: '0.85rem' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          '@media (prefers-color-scheme: dark)': {
            color: '#00C4A7',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', padding: '10px 22px', fontWeight: 600 },
        containedPrimary: {
          background: 'linear-gradient(135deg, #0D5C54 0%, #00897B 100%)',
          boxShadow:  '0 4px 14px rgba(0,137,123,0.32)',
          color: '#ffffff',
          '&:hover': {
            background: 'linear-gradient(135deg, #0a4f48 0%, #007a6d 100%)',
            boxShadow:  '0 6px 20px rgba(0,137,123,0.42)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)',
          boxShadow:  '0 4px 14px rgba(124,58,237,0.28)',
          '&:hover': {
            background: 'linear-gradient(135deg, #6d31d4 0%, #9746e8 100%)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: prefersDark ? '#104448' : '#FFFFFF',
          border:    prefersDark ? '1px solid #1A5A68' : '1px solid #B2DDD8',
          boxShadow: prefersDark ? 'none' : '0 1px 3px rgba(0,137,123,0.07), 0 4px 14px rgba(0,196,167,0.05)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: prefersDark ? '#0D3840' : '#FFFFFF',
          border:       prefersDark ? '1px solid #1A5A68' : '1px solid #B2DDD8',
          borderRadius: 20,
          boxShadow:    prefersDark
            ? '0 8px 40px rgba(0,0,0,0.5)'
            : '0 8px 40px rgba(0,137,123,0.14), 0 24px 64px rgba(0,137,123,0.06)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, border: '1px solid' },
        standardWarning: { backgroundColor: '#FFFBEB', borderColor: '#FCD34D', color: '#92400E' },
        standardError:   { backgroundColor: '#FEF2F2', borderColor: '#FCA5A5', color: '#991B1B' },
        standardSuccess: { backgroundColor: '#ECFDF5', borderColor: '#6EE7B7', color: '#065F46' },
        standardInfo:    { backgroundColor: '#F0FDF9', borderColor: '#6EE7D0', color: '#0D5C54' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'transparent',
          borderBottom:    'none',
          boxShadow:       'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        @media (prefers-color-scheme: dark) {
          body {
            background-color: #0B2D36 !important;
            color: #00C4A7 !important;
          }
          .MuiPaper-root {
            background-color: #104448 !important;
            border-color: #1A5A68 !important;
          }
          .MuiDialog-paper {
            background-color: #0D3840 !important;
          }
          .MuiMenu-paper {
            background-color: #0D3840 !important;
          }
        }
      `,
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: { color: '#00897B' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 100, background: 'rgba(0,137,123,0.10)' },
        bar:  { background: 'linear-gradient(90deg, #0D5C54, #00897B)' },
      },
    },
  },
});

// Fallback default export per compatibilità con eventuali import diretti
export default createAppTheme(false);
